import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { formTemplateApi } from '../../services/formTemplates';

export default function TemplateVersionDialog({
  open,
  onOpenChange,
  template,
  token,
  isOwner,
  onSelectVersion,
  onDuplicate,
  onShare,
  onPreview,
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // track per-action pending

  useEffect(() => {
    if (open && template?.id) {
      setLoading(true);
      formTemplateApi
        .listVersions(template.id, token)
        .then(res => setVersions(res))
        .catch(err => toast.error(err.response?.data?.detail || 'Failed to load version history'))
        .finally(() => setLoading(false));
    }
  }, [open, template, token]);

  if (!template) return null;

  const displayVersions = isOwner
    ? versions
    : versions.filter(v => {
        if (v.status !== 'published') return false;
        if (template.active_version_id && versions.some(ver => ver.id === template.active_version_id)) {
          return v.id === template.active_version_id;
        }
        if (template.active_version_number && versions.some(ver => ver.version_number === template.active_version_number)) {
          return v.version_number === template.active_version_number;
        }
        return true;
      });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Version history — {template.name}</DialogTitle>
          <DialogDescription>
            View published versions, select which version to use for new assessments, or duplicate a version as a new template.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {loading ? (
            <p className="text-sm text-slate-500">Loading versions…</p>
          ) : displayVersions.length === 0 ? (
            <p className="text-sm text-slate-500">No versions available.</p>
          ) : (
            <div className="space-y-3">
              {displayVersions.map(v => {
                const isActive = template.active_version_id === v.id;
                return (
                  <div
                    key={v.id}
                    className="flex flex-col gap-2 p-3 border rounded bg-slate-50 text-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">
                            {v.status === 'published' ? `Version ${v.version_number}` : 'Draft'}
                          </span>
                          {isActive && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">
                              Used for new assessments
                            </span>
                          )}
                          {v.status === 'draft' && (
                            <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded">
                              Draft
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {v.section_count} sections, {v.field_count} fields
                          {v.published_at && ` • Published ${new Date(v.published_at).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    {v.status === 'published' && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {isOwner && !isActive && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Use Version ${v.version_number} for new assessments`}
                            disabled={pendingAction === `select-${v.id}`}
                            onClick={async () => {
                              setPendingAction(`select-${v.id}`);
                              try { await onSelectVersion(template.id, v.id); } finally { setPendingAction(null); }
                            }}
                          >
                            Use for new assessments
                          </Button>
                        )}
                        {onDuplicate && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Duplicate Version ${v.version_number} as new template`}
                            disabled={pendingAction === `dup-${v.id}`}
                            onClick={() => onDuplicate(v.id, v.version_number)}
                          >
                            Duplicate as new template
                          </Button>
                        )}
                        {onShare && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Share Version ${v.version_number} with clinic`}
                            disabled={pendingAction === `share-${v.id}`}
                            onClick={async () => {
                              setPendingAction(`share-${v.id}`);
                              try { await onShare(v.id); } finally { setPendingAction(null); }
                            }}
                          >
                            Share copy with clinic
                          </Button>
                        )}
                        {onPreview && v.status === 'published' && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Preview Version ${v.version_number}`}
                            onClick={() => onPreview(v.id)}
                          >
                            Preview
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

TemplateVersionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  template: PropTypes.object,
  token: PropTypes.string.isRequired,
  isOwner: PropTypes.bool.isRequired,
  onSelectVersion: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func,
  onShare: PropTypes.func,
  onPreview: PropTypes.func,
};
